import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { base44 } from "@/api/base44Client";
import { ChevronLeft, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import PortSelector from "@/components/port-entry/PortSelector";
import TripContextSetup from "@/components/port-entry/TripContextSetup";
import PortEntryPackForm from "@/components/port-entry/PortEntryPackForm";
import SubmissionConfirmation from "@/components/port-entry/SubmissionConfirmation";
import PortEntryHistory from "@/components/port-entry/PortEntryHistory";
import DisplayAd from "@/components/ads/DisplayAd";
import { AD_SLOTS } from "@/config/adSlots";
import HeroLayout from "@/components/hero/HeroLayout";

const STEPS = [
  { id: "port-select", title: "Select Port", icon: "🌍" },
  { id: "trip-context", title: "Trip Details", icon: "🚤" },
  { id: "entry-pack", title: "Entry Pack", icon: "📄" },
  { id: "confirmation", title: "Confirmed", icon: "✔️" },
  { id: "history", title: "History", icon: "📊" }
];

export default function PortEntryPack() {
  const [step, setStep] = useState(0);
  const [user, setUser] = useState(null);
  const [selectedPort, setSelectedPort] = useState(null);
  const [tripData, setTripData] = useState(null);
  const [entryPackData, setEntryPackData] = useState(null);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);

  useEffect(() => {
    base44.auth.me().then(setUser).catch(() => setUser(null));
  }, []);

  const handlePortSelect = (port) => {
    setSelectedPort(port);
    setStep(1);
  };

  const handleTripContextSave = (data) => {
    setTripData(data);
    setStep(2);
  };

  const handleEntryPackSubmit = (data) => {
    setEntryPackData(data);
    setStep(3);
  };

  const handleConfirmationComplete = () => {
    setStep(4);
  };

  const handleReset = () => {
    setStep(0);
    setSelectedPort(null);
    setTripData(null);
    setEntryPackData(null);
    setSubmissionConfirmed(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 pb-20">
      <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-6 lg:grid-cols-[160px_minmax(0,56rem)] lg:items-start">
        <aside className="hidden lg:block sticky top-6">
          <DisplayAd slot={AD_SLOTS.banner} />
        </aside>

        <main className="min-w-0 space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <HeroLayout
            overlay="default"
            eyebrow={step > 0 ? <Button variant="outline" size="icon" onClick={() => setStep(step - 1)} className="rounded-full border-white/50 text-white hover:bg-white hover:text-ocean"><ChevronLeft className="w-4 h-4" /></Button> : <span className="inline-flex rounded-full bg-white/12 px-3 py-1.5 text-xs font-semibold text-white backdrop-blur-md">Digital Documentation</span>}
            title="⚓ Port Entry Pack"
            subtitle="Generate complete digital entry documentation in minutes"
          />
        </motion.div>

        {/* Progress Steps */}
        <motion.div
          className="flex gap-2 overflow-x-auto pb-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {STEPS.map((s, idx) => (
            <motion.button
              key={s.id}
              onClick={() => idx <= step && setStep(idx)}
              className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                idx === step
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : idx < step
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-muted text-muted-foreground"
              }`}
              whileHover={{ scale: 1.05 }}
            >
              <span>{s.icon}</span>
              <span className="hidden sm:inline">{s.title}</span>
            </motion.button>
          ))}
        </motion.div>

        {/* Content Area */}
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step === 0 && <PortSelector onSelect={handlePortSelect} />}

            {step === 1 && selectedPort && (
              <TripContextSetup
                port={selectedPort}
                user={user}
                onSave={handleTripContextSave}
                onBack={() => setStep(0)}
              />
            )}

            {step === 2 && selectedPort && tripData && (
              <PortEntryPackForm
                port={selectedPort}
                trip={tripData}
                user={user}
                onSubmit={handleEntryPackSubmit}
                onBack={() => setStep(1)}
              />
            )}

            {step === 3 && entryPackData && (
              <SubmissionConfirmation
                port={selectedPort}
                tripData={tripData}
                entryPackData={entryPackData}
                onComplete={handleConfirmationComplete}
                onBack={() => setStep(2)}
              />
            )}

            {step === 4 && (
              <PortEntryHistory
                user={user}
                onCreateNew={handleReset}
              />
            )}
          </motion.div>
        </AnimatePresence>
        </main>
      </div>
    </div>
  );
}